import api from '../api/axios'
import {
  Bien, BienEnBaja, DatosBien, FichaBien, FichaPublica, FiltrosBienes, Movimiento, Pagina, PersonaMini, Plancheta, Recinto, Resumen, Sector, SolicitudTraslado,
} from '../types/inventario'

/** Las rutas de un recinto van por su código (N3-OF310). */
const r = (codigo: string) => `recintos/${encodeURIComponent(codigo)}/`

export const inventarioService = {
  resumen: async (): Promise<Resumen> => (await api.get<Resumen>('inventario/resumen/')).data,

  recintos: async (): Promise<Recinto[]> => (await api.get<Recinto[]>('recintos/')).data,
  recinto: async (codigo: string): Promise<Recinto> => (await api.get<Recinto>(r(codigo))).data,
  crearRecinto: async (datos: { codigo: string; nombre: string; sector: Sector; piso: string }): Promise<Recinto> =>
    (await api.post<Recinto>('recintos/', datos)).data,
  bienesDeRecinto: async (codigo: string): Promise<Bien[]> => (await api.get<Bien[]>(`${r(codigo)}bienes/`)).data,
  historialRecinto: async (codigo: string): Promise<Movimiento[]> => (await api.get<Movimiento[]>(`${r(codigo)}historial/`)).data,
  cambiarEncargado: async (codigo: string, persona: number | null, motivo = ''): Promise<Recinto> =>
    (await api.put<Recinto>(`${r(codigo)}encargado/`, { persona, motivo })).data,

  plancheta: async (codigo: string): Promise<Plancheta> => (await api.get<Plancheta>(`${r(codigo)}plancheta/`)).data,
  imprimirPlancheta: async (codigo: string): Promise<Plancheta> => (await api.post<Plancheta>(`${r(codigo)}plancheta/imprimir/`)).data,
  marcarPlancheta: async (codigo: string, estado: string, nota: string): Promise<Recinto> =>
    (await api.post<Recinto>(`${r(codigo)}plancheta/estado/`, { estado, nota })).data,

  bienes: async (filtros: FiltrosBienes): Promise<Pagina<Bien>> => (await api.get<Pagina<Bien>>('bienes/', { params: filtros })).data,
  buscar: async (q: string): Promise<Bien[]> => (await api.get<Bien[]>('bienes/buscar/', { params: { q } })).data,
  bien: async (id: number): Promise<FichaBien> => (await api.get<FichaBien>(`bienes/${id}/`)).data,
  /** La ficha que abre el QR. */
  fichaPublica: async (clave: string): Promise<FichaPublica> => (await api.get<FichaPublica>(`b/${encodeURIComponent(clave)}/`)).data,
  historialBien: async (id: number): Promise<Movimiento[]> => (await api.get<Movimiento[]>(`bienes/${id}/historial/`)).data,
  registrarBien: async (recinto: string, datos: DatosBien): Promise<FichaBien> =>
    (await api.post<FichaBien>('bienes/', { ...datos, recinto })).data,
  editarBien: async (id: number, datos: DatosBien & { motivo?: string }): Promise<FichaBien> =>
    (await api.patch<FichaBien>(`bienes/${id}/`, datos)).data,
  trasladar: async (bienes: number[], destino: string, motivo = ''): Promise<Bien[]> =>
    (await api.post<Bien[]>('bienes/trasladar/', { bienes, destino, motivo })).data,
  marcarNoUbicados: async (bienes: number[], motivo: string): Promise<Bien[]> =>
    (await api.post<Bien[]>('bienes/no-ubicados/', { bienes, motivo })).data,
  darDeBaja: async (bienes: number[], motivo: string): Promise<Bien[]> => (await api.post<Bien[]>('bienes/baja/', { bienes, motivo })).data,
  revertirBaja: async (bienes: number[], motivo = ''): Promise<Bien[]> =>
    (await api.post<Bien[]>('bienes/revertir-baja/', { bienes, motivo })).data,
  bajas: async (): Promise<BienEnBaja[]> => (await api.get<BienEnBaja[]>('bajas/')).data,
  /** Les falta la etiqueta (`pendiente`) o ya la tienen pegada. */
  marcarEtiquetas: async (bienes: number[], pendiente: boolean): Promise<Bien[]> =>
    (await api.post<Bien[]>('bienes/etiqueta/', { bienes, pendiente })).data,
  /** Todos los bienes de un filtro (sin páginas), para imprimir sus etiquetas. */
  paraEtiquetas: async (filtros: FiltrosBienes): Promise<Bien[]> => (await api.get<Bien[]>('etiquetas/', { params: filtros })).data,
  marcarNoUbicado: async (id: number, motivo: string): Promise<FichaBien> =>
    (await api.post<FichaBien>(`bienes/${id}/no-ubicado/`, { motivo })).data,
  marcarUbicado: async (id: number, recinto: string, motivo = ''): Promise<FichaBien> =>
    (await api.post<FichaBien>(`bienes/${id}/ubicado/`, { recinto, motivo })).data,
}

/** Solicitudes de traslado: quien administra bienes ve todas; el resto, las suyas. */
export const solicitudService = {
  lista: async (estado?: 'PENDIENTE' | 'RESUELTAS'): Promise<SolicitudTraslado[]> =>
    (await api.get<SolicitudTraslado[]>('solicitudes/', { params: estado ? { estado } : {} })).data,
  pedir: async (bienes: number[], destino: string, motivo: string): Promise<SolicitudTraslado> =>
    (await api.post<SolicitudTraslado>('solicitudes/', { bienes, destino, motivo })).data,
  aprobar: async (id: number, respuesta = ''): Promise<SolicitudTraslado> =>
    (await api.post<SolicitudTraslado>(`solicitudes/${id}/aprobar/`, { respuesta })).data,
  rechazar: async (id: number, respuesta: string): Promise<SolicitudTraslado> =>
    (await api.post<SolicitudTraslado>(`solicitudes/${id}/rechazar/`, { respuesta })).data,
  anular: async (id: number): Promise<SolicitudTraslado> => (await api.post<SolicitudTraslado>(`solicitudes/${id}/anular/`, {})).data,
}

export const personaService = {
  buscar: async (q: string): Promise<PersonaMini[]> => (await api.get<PersonaMini[]>('personas/', { params: { q } })).data,
  /** La agrega por RUT (con sus datos de UCampus si Puente responde; si no, con los nombres dados). */
  agregar: async (datos: { rut: string; nombres?: string; apellidos?: string }): Promise<PersonaMini> =>
    (await api.post<PersonaMini>('personas/', datos)).data,
}
